import React, {useEffect, useState} from 'react';
import {NavLink} from 'react-router-dom';
import useUser from '@/hooks/useUser';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Box,
    Divider,
    Typography,
    Tooltip,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PieChartIcon from '@mui/icons-material/PieChart';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import EventIcon from '@mui/icons-material/Event';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// @ts-ignore — JS modules
import {
    AdminHome,
    Alarms,
    ApplicationEditorRoot,
    DashboardsRoot,
    MonitoringGroups,
    NotificationList,
    PlatformRoot,
    RemusEventStatistics,
    ReportingHome,
    StatisticsHome,
    Trends,
} from '@/routes';
import {isAppMode} from '@/config/mockAuth';
// @ts-ignore — JS module
import {initSelectors} from '@/behavior/init';
// @ts-ignore — JS module
import usePlatform from '@/hooks/usePlatform';
// @ts-ignore — JS module
import useIsApplicationAdmin from '@/behavior/applicationEditor/hooks/permissions/useIsApplicationAdmin';
import {openInNewTab} from '@/utils/openInNewTab';
import './Sidebar.css';

/** Monogram icon for external tools that have no good glyph (Grafana, HelpAlarm).
 *  Sized to match the 24px MUI icons and inherits the sidebar icon color. */
const LetterIcon = ({letter}: {letter: string}) => (
    <Box
        component="span"
        aria-hidden
        sx={{
            width: 24,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            fontWeight: 800,
            lineHeight: 1,
            fontFamily: '"Nunito Sans", sans-serif',
        }}
    >
        {letter}
    </Box>
);

interface SidebarProps {
    isExpanded: boolean;
    onToggle: () => void;
}

/** Persists the Electron "Show more" toggle across launches, mirroring the
 *  localStorage approach used for the light/dark theme. Best-effort: storage
 *  can throw in sandboxed/private contexts, so reads/writes are guarded. */
const SHOW_MORE_STORAGE_KEY = 'remus-sidebar-show-more';

const getStoredShowMore = (): boolean => {
    try {
        return window.localStorage.getItem(SHOW_MORE_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

interface MenuItemDef {
    key: string;
    label: string;
    icon: React.ReactElement;
    path?: string;
    onClick?: () => void;
    visible?: boolean;
    end?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({isExpanded, onToggle}) => {
    const platform = usePlatform();
    const user = useUser();
    const isApplicationAdmin = useIsApplicationAdmin();
    const isApp = isAppMode();
    const [showMore, setShowMore] = useState(getStoredShowMore);

    useEffect(() => {
        try {
            window.localStorage.setItem(SHOW_MORE_STORAGE_KEY, String(showMore));
        } catch {
            // Persistence is best-effort; the in-memory state still works.
        }
    }, [showMore]);

    // App build (Electron): same destinations as the web app, but the
    // non-essential ones are tucked behind "Show more" so the desktop app stays
    // simple on first glance. No Home — the app lands on Trends.
    const appPrimaryItems: MenuItemDef[] = [
        {
            key: 'trends',
            label: 'Trends',
            icon: <ShowChartIcon/>,
            path: Trends.getLink({platform}, null, false),
        },
        {
            key: 'monitoring-groups',
            label: 'Monitoring Groups',
            icon: <ListAltIcon/>,
            path: MonitoringGroups.getLink({platform}, null, false),
        },
        {
            key: 'statistics-events',
            label: 'Events',
            icon: <EventIcon/>,
            path: RemusEventStatistics.getLink({platform}, null, false),
        },
        {
            key: 'notifications',
            label: 'Notifications',
            icon: <NotificationsIcon/>,
            path: NotificationList.getLink({platform}, null, false),
        },
        {
            key: 'application-editor',
            label: 'Application Editor',
            icon: <EditIcon/>,
            path: ApplicationEditorRoot.getLink({platform}, null, false),
            visible: isApplicationAdmin,
        },
    ];

    const appSecondaryItems: MenuItemDef[] = [
        {
            key: 'dashboards',
            label: 'Dashboards',
            icon: <DashboardIcon/>,
            path: DashboardsRoot.getLink({platform}, null, false),
        },
        {
            key: 'alarms',
            label: 'Alarms',
            icon: <WarningAmberIcon/>,
            path: Alarms.getLink({platform}, null, false),
        },
        {
            key: 'statistics',
            label: 'Statistics',
            icon: <PieChartIcon/>,
            path: StatisticsHome.getLink({platform}, null, false),
        },
        {
            key: 'reporting',
            label: 'Reporting',
            icon: <BusinessCenterIcon/>,
            path: ReportingHome.getLink({platform}, null, false),
        },
        {
            key: 'admin',
            label: 'Admin',
            icon: <SettingsIcon/>,
            path: AdminHome.getLink({platform}, null, false),
            visible: initSelectors.isAdmin(user),
        },
    ];

    const internalItems: MenuItemDef[] = [
        {
            key: 'home',
            label: 'Home',
            icon: <HomeIcon/>,
            path: PlatformRoot.getLink({platform}, null, false),
            end: true,
        },
        {
            key: 'trends',
            label: 'Trends',
            icon: <ShowChartIcon/>,
            path: Trends.getLink({platform}, null, false),
        },
        {
            key: 'alarms',
            label: 'Alarms',
            icon: <WarningAmberIcon/>,
            path: Alarms.getLink({platform}, null, false),
        },
        {
            key: 'dashboards',
            label: 'Dashboards',
            icon: <DashboardIcon/>,
            path: DashboardsRoot.getLink({platform}, null, false),
        },
        {
            key: 'statistics',
            label: 'Statistics',
            icon: <PieChartIcon/>,
            path: StatisticsHome.getLink({platform}, null, false),
        },
        {
            key: 'reporting',
            label: 'Reporting',
            icon: <BusinessCenterIcon/>,
            path: ReportingHome.getLink({platform}, null, false),
        },
        {
            key: 'notifications',
            label: 'Notifications',
            icon: <NotificationsIcon/>,
            path: NotificationList.getLink({platform}, null, false),
        },
        {
            key: 'monitoring-groups',
            label: 'Monitoring Groups',
            icon: <ListAltIcon/>,
            path: MonitoringGroups.getLink({platform}, null, false),
        },
        {
            key: 'application-editor',
            label: 'Application Editor',
            icon: <EditIcon/>,
            path: ApplicationEditorRoot.getLink({platform}, null, false),
            visible: isApplicationAdmin,
        },
        {
            key: 'admin',
            label: 'Admin',
            icon: <SettingsIcon/>,
            path: AdminHome.getLink({platform}, null, false),
            visible: initSelectors.isAdmin(user),
        },
    ];

    const externalItems: MenuItemDef[] = [
        {
            key: 'grafana',
            label: 'Grafana',
            icon: <LetterIcon letter="G"/>,
            onClick: () =>
                openInNewTab(
                    `https://monit-grafana.cern.ch/d/omhwpE171${
                        platform === 'TEST' ? '-test' : ''
                    }/?orgId=120`
                ),
        },
        {
            key: 'rog',
            label: 'ROG',
            icon: <WebAssetIcon/>,
            onClick: () => openInNewTab('https://rog.cern.ch'),
        },
        {
            key: 'timber',
            label: 'Timber',
            icon: <StackedLineChartIcon/>,
            onClick: () => openInNewTab('https://timber.cern.ch'),
        },
        {
            key: 'helpalarm',
            label: 'HelpAlarm',
            icon: <LetterIcon letter="H"/>,
            onClick: () => openInNewTab('https://helpalarm.cern.ch'),
        },
    ];

    const renderItem = (item: MenuItemDef) => {
        if (item.visible === false) return null;

        const buttonProps: any = {
            className: 'sidebar-item',
            'aria-label': item.label,
        };
        if (item.path) {
            buttonProps.component = NavLink;
            buttonProps.to = item.path;
            buttonProps.end = item.end;
        }
        if (item.onClick) {
            buttonProps.onClick = item.onClick;
        }

        return (
            <ListItem key={item.key} disablePadding className="sidebar-list-item">
                <Tooltip
                    title={item.label}
                    placement="right"
                    disableHoverListener={isExpanded}
                    disableFocusListener={isExpanded}
                    disableTouchListener={isExpanded}
                >
                    <ListItemButton {...buttonProps}>
                        <ListItemIcon className="sidebar-icon">{item.icon}</ListItemIcon>
                        {isExpanded && <ListItemText primary={item.label} className="sidebar-text"/>}
                    </ListItemButton>
                </Tooltip>
            </ListItem>
        );
    };

    const showMoreToggle: MenuItemDef = {
        key: 'show-more',
        label: showMore ? 'Show less' : 'Show more',
        icon: showMore ? <ExpandLessIcon/> : <ExpandMoreIcon/>,
        onClick: () => setShowMore(value => !value),
    };

    return (
        <>
            {isExpanded && <Box className="sidebar-backdrop" onClick={onToggle}/>}
            <Drawer
                variant="permanent"
                className={`sidebar-drawer ${isExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'}`}
            >
                <Box className="sidebar-container" data-tutorial="sidebar">
                    <Box className="sidebar-header">
                        {isExpanded ? (
                            <>
                                <span className="sidebar-logo">Menu</span>
                                <IconButton
                                    onClick={onToggle}
                                    className="sidebar-toggle-btn"
                                    size="small"
                                    aria-label="Collapse navigation menu"
                                    title="Collapse navigation menu"
                                >
                                    <ChevronLeftIcon/>
                                </IconButton>
                            </>
                        ) : (
                            <IconButton
                                onClick={onToggle}
                                className="sidebar-toggle-btn"
                                size="small"
                                aria-label="Expand navigation menu"
                                title="Expand navigation menu"
                            >
                                <MenuIcon/>
                            </IconButton>
                        )}
                    </Box>

                    <List className="sidebar-list">
                        {isApp ? (
                            <>
                                {appPrimaryItems.map(renderItem)}
                                {renderItem(showMoreToggle)}
                                {showMore && appSecondaryItems.map(renderItem)}
                                {showMore && <Divider className="sidebar-divider"/>}
                                {showMore && isExpanded && (
                                    <Typography variant="caption" className="sidebar-section-label">
                                        External links
                                    </Typography>
                                )}
                                {showMore && externalItems.map(renderItem)}
                            </>
                        ) : (
                            <>
                                {internalItems.map(renderItem)}
                                <Divider className="sidebar-divider"/>
                                {isExpanded && (
                                    <Typography variant="caption" className="sidebar-section-label">
                                        External links
                                    </Typography>
                                )}
                                {externalItems.map(renderItem)}
                            </>
                        )}
                    </List>
                </Box>
            </Drawer>
        </>
    );
};

export default Sidebar;
